import { triggerBlobDownload } from './fileDownload';

/**
 * Exports CSV lisibles par Excel en français : séparateur `;`, BOM UTF-8 (sans
 * lui, Excel lit les accents en Latin-1) et décimales à virgule.
 */

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

const escapeCell = (value: string): string =>
  /[;"\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Un texte commençant par `= + - @` (ou tabulation, retour chariot) serait exécuté
 * comme formule par Excel : on le préfixe d'une apostrophe, comme l'API
 * (common/utils/csv.util). Les nombres passent tels quels.
 */
const neutralizeFormula = (value: string): string =>
  /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

const toCell = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '';
  return escapeCell(typeof value === 'number' ? String(value) : neutralizeFormula(value));
};

export function buildCsv<T>(rows: T[], columns: CsvColumn<T>[]): string {
  const header = columns.map((column) => escapeCell(column.header)).join(';');
  const lines = rows.map((row) => columns.map((column) => toCell(column.value(row))).join(';'));
  return '﻿' + [header, ...lines].join('\n');
}

export function downloadCsv<T>(rows: T[], columns: CsvColumn<T>[], fileName: string): void {
  const blob = new Blob([buildCsv(rows, columns)], { type: 'text/csv;charset=utf-8;' });
  triggerBlobDownload(blob, fileName);
}

/** Montant en euros → « 1234,50 » (vide si absent). */
export const csvAmount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : value.toFixed(2).replace('.', ',');

/** Date du jour « AAAA-MM-JJ », pour nommer les fichiers. */
export const csvFileDate = (): string => new Date().toISOString().slice(0, 10);
